import { type FC, type PropsWithChildren, useCallback } from "react"
import { type ButtonProps, Stack } from "@octopus-energy/coral/atoms"
import { IconArrowRightFromBracket } from "@octopus-energy/icons"
import { useTranslations } from "next-intl"
import { useToast } from "@/hooks/useToast"
import { useLogout, useSession } from "@/lib/blueprint-auth/client"
import { NavigationMenu } from "../NavigationMenu"

type LogoutButtonVariant = "dropdown" | "sub"

const LogoutButtonComponents: Record<LogoutButtonVariant, FC<PropsWithChildren<ButtonProps>>> = {
    dropdown: NavigationMenu.Dropdown.Button,
    sub: NavigationMenu.Sub.Button,
}

export function LogoutButton({ variant }: { variant: LogoutButtonVariant }) {
    const t = useTranslations("LogoutButton")
    const logout = useLogout()
    const session = useSession()
    const toast = useToast()

    const handleLogout = useCallback(async () => {
        try {
            await logout.mutateAsync()
            await session.refetch()
        } catch {
            return toast("error.common", { type: "error" })
        }
    }, [logout, session, toast])

    const Button = LogoutButtonComponents[variant]

    return (
        <Button
            onClick={handleLogout}
            attributes={{ "data-testid": "account-menu-dropdown-item-logout" }}
        >
            <Stack
                alignItems="center"
                gap="sm"
            >
                <IconArrowRightFromBracket color="inherit" />
                {t("logout")}
            </Stack>
        </Button>
    )
}

LogoutButton.messages = ["LogoutButton", ...useToast.messages] as const
